import React from 'react';
import HighlightedPdfViewer from './HighlightedPdfViewer';

interface CitationData {
  documentId: string;
  documentName: string;
  page: number;
  startOffset: number;
  endOffset: number;
  text: string;
  context?: string;
  confidence?: number; // 0..1, optional fuzzy-match confidence from backend
}

interface CitationSibling {
  docId: string;
  docName: string;
  page: string | number;
  text?: string;
  confidence?: number;
  sectionSiblingIndex: number;
}

interface UnifiedPdfCitationViewerProps {
  documentId: string;
  citation: CitationData;
  onClose: () => void;
  useAnnotationViewer?: boolean; // Legacy prop, now ignored
  /** Other citations in the same section, used to render prev/next nav. */
  siblings?: CitationSibling[];
  siblingIndex?: number;
  onNavigate?: (sibling: CitationSibling) => void;
}

/**
 * Simplified Unified PDF Citation Viewer
 * 
 * This component has been simplified to only use HighlightedPdfViewer
 * to avoid PDF.js version conflicts between different PDF libraries.
 * 
 * All citation viewing now uses the reliable react-pdf + text-based highlighting approach.
 */
export const UnifiedPdfCitationViewer: React.FC<UnifiedPdfCitationViewerProps> = ({
  documentId,
  citation,
  onClose,
  useAnnotationViewer = false, // Ignored for now
  siblings,
  siblingIndex,
  onNavigate,
}) => {
  const token = localStorage.getItem('riskninja-token');

  // Handle escape key - MUST be before any early returns
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const [copied, setCopied] = React.useState(false);
  const [openingOriginal, setOpeningOriginal] = React.useState(false);
  // Live match info from the highlighter — overrides citation.confidence so
  // the sidebar pill reflects whether the highlight actually landed.
  const [matchInfo, setMatchInfo] = React.useState<{ found: boolean; confidence: number } | null>(null);

  // Stabilize props passed to the heavy PDF viewer. Without these, every
  // setMatchInfo would re-render the modal, re-create the citations array
  // literal, and remount the highlighter — wiping the position state right
  // after the matcher found it.
  const pdfCitations = React.useMemo(
    () => [
      {
        documentId: citation.documentId,
        page: citation.page,
        startOffset: citation.startOffset,
        endOffset: citation.endOffset,
        text: citation.text,
      },
    ],
    [
      citation.documentId,
      citation.page,
      citation.startOffset,
      citation.endOffset,
      citation.text,
    ]
  );
  const handlePdfError = React.useCallback(
    (err: string) => console.error('PDF Viewer error:', err),
    []
  );
  const handlePdfMatchInfo = React.useCallback(
    (info: { found: boolean; confidence: number }) =>
      setMatchInfo({ found: info.found, confidence: info.confidence }),
    []
  );

  // Early return AFTER all hooks (rules-of-hooks): hooks must run on every render.
  if (!token) {
    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-4 text-red-600">Authentication Required</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">Please log in to view citations.</p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const copyQuote = async () => {
    try {
      await navigator.clipboard.writeText(citation.text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Fetch the original PDF (with auth) and open it in a new tab. The
  // /api/documents/:id/stream endpoint requires the bearer token, so a plain
  // <a href> won't work.
  const openOriginal = async () => {
    setOpeningOriginal(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_API_URL}/api/documents/${documentId}/stream`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Don't revoke immediately — the new tab needs the URL to stay alive.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Failed to open original PDF:', err);
      alert('Could not open the original PDF.');
    } finally {
      setOpeningOriginal(false);
    }
  };

  // Prefer live match info from the page-image endpoint; fall back to the
  // confidence the citation was created with (may be undefined).
  const effectiveConfidence =
    matchInfo
      ? (matchInfo.found ? matchInfo.confidence : 0)
      : typeof citation.confidence === 'number'
      ? citation.confidence
      : null;
  const confidencePct =
    typeof effectiveConfidence === 'number' ? Math.round(effectiveConfidence * 100) : null;
  const matchPill =
    confidencePct === null
      ? { label: 'Unverified match', tone: 'gray' as const }
      : confidencePct >= 95
      ? { label: 'Exact match', tone: 'green' as const }
      : confidencePct >= 60
      ? { label: `Fuzzy match · ${confidencePct}%`, tone: 'amber' as const }
      : { label: 'Quote not located', tone: 'red' as const };

  const pillToneClass = {
    green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
    gray: 'bg-gray-50 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300 border-gray-200 dark:border-gray-600',
  }[matchPill.tone];

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75">
      <div className="h-full flex">
        {/* Sidebar — leads with the quote, then metadata, then actions */}
        <div className="w-80 bg-white dark:bg-gray-800 p-5 overflow-y-auto border-r border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              Citation
              {siblings && siblings.length > 1 && typeof siblingIndex === 'number' && (
                <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                  {siblingIndex + 1} of {siblings.length}
                </span>
              )}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Close (Esc)"
              aria-label="Close citation viewer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Prev / Next walk through citations in the same section without
              closing the modal. */}
          {siblings && siblings.length > 1 && typeof siblingIndex === 'number' && onNavigate && (
            <div className="flex items-center gap-1 mb-3">
              <button
                disabled={siblingIndex === 0}
                onClick={() => onNavigate(siblings[siblingIndex - 1])}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Previous citation"
                aria-label="Previous citation"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Prev
              </button>
              <button
                disabled={siblingIndex >= siblings.length - 1}
                onClick={() => onNavigate(siblings[siblingIndex + 1])}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Next citation"
                aria-label="Next citation"
              >
                Next
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Quoted text — the actual verification artifact */}
          {citation.text && citation.text.trim().length > 0 ? (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                Quoted text
              </div>
              <blockquote className="border-l-4 border-blue-500 bg-blue-50/60 dark:bg-blue-900/20 text-sm text-gray-800 dark:text-gray-200 p-3 rounded-r leading-relaxed">
                {citation.text}
              </blockquote>
              <button
                onClick={copyQuote}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-200"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1m-6-8h6a2 2 0 012 2v6a2 2 0 01-2 2h-6a2 2 0 01-2-2v-6a2 2 0 012-2z" />
                </svg>
                {copied ? 'Copied' : 'Copy quote'}
              </button>
            </div>
          ) : (
            <div className="mb-4 text-xs text-gray-500 dark:text-gray-400 italic">
              No quoted text recorded for this citation.
            </div>
          )}

          {/* Match status */}
          <div className="mb-4">
            <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium ${pillToneClass}`}>
              {matchPill.tone === 'green' && (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {matchPill.tone === 'amber' && (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
                </svg>
              )}
              {matchPill.tone === 'red' && (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {matchPill.label}
            </div>
          </div>

          {/* Source metadata */}
          <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1.5 mb-5">
            <div className="flex gap-2">
              <span className="text-gray-500 dark:text-gray-400 min-w-[60px]">Document</span>
              <span className="font-medium break-words">{citation.documentName}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-gray-500 dark:text-gray-400 min-w-[60px]">Page</span>
              <span className="font-medium">{citation.page}</span>
            </div>
          </div>

          {/* View original PDF */}
          <button
            onClick={openOriginal}
            disabled={openingOriginal}
            className="inline-flex items-center justify-center gap-1.5 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            {openingOriginal ? 'Opening…' : 'View original PDF'}
          </button>
        </div>

        {/* PDF viewer — react-pdf with the client-side fuzzy text highlighter.
            We tried a server-rendered PNG path (CitationPageImage / the
            /api/citations/page-image endpoint) but its match rate is below
            the client-side highlighter today, so we keep that endpoint and
            component in the codebase as a future option and use the PDF
            viewer here. */}
        <div className="flex-1">
          <HighlightedPdfViewer
            documentId={documentId}
            citations={pdfCitations}
            authToken={token}
            onError={handlePdfError}
            onMatchInfo={handlePdfMatchInfo}
          />
        </div>
      </div>
    </div>
  );
};

export default UnifiedPdfCitationViewer;